import React, { useRef, useState } from 'react'
import { ProfilePoint } from '../utils/elevationProfile'

interface Props {
  data: ProfilePoint[]
  color?: string
}

const W = 320
const H = 120
const PAD = { top: 8, right: 6, bottom: 18, left: 32 }

// Lightweight, dependency-free elevation-vs-distance area chart with a
// hover tooltip. Scales to its container width via the SVG viewBox.
const ElevationProfile: React.FC<Props> = ({ data, color = '#3b82f6' }) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const [hover, setHover] = useState<number | null>(null)

  if (!data || data.length < 2) {
    return <div className="text-sm text-gray-500">No elevation data for this day.</div>
  }

  const minD = data[0].d
  const maxD = data[data.length - 1].d
  const elevations = data.map((p) => p.e)
  const minE = Math.min(...elevations)
  let maxE = Math.max(...elevations)
  if (maxE === minE) maxE = minE + 1
  const range = maxE - minE
  const yMin = minE - range * 0.08
  const yMax = maxE + range * 0.08

  const plotW = W - PAD.left - PAD.right
  const plotH = H - PAD.top - PAD.bottom
  const baseY = PAD.top + plotH

  const x = (d: number) => PAD.left + ((d - minD) / (maxD - minD || 1)) * plotW
  const y = (e: number) => PAD.top + (1 - (e - yMin) / (yMax - yMin || 1)) * plotH

  const line = data
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.d).toFixed(1)},${y(p.e).toFixed(1)}`)
    .join(' ')
  const area = `${line} L${x(maxD).toFixed(1)},${baseY.toFixed(1)} L${x(minD).toFixed(1)},${baseY.toFixed(1)} Z`

  const handleMove = (ev: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect) return
    const px = ((ev.clientX - rect.left) / rect.width) * W
    const dVal = minD + ((px - PAD.left) / plotW) * (maxD - minD)
    // binary search nearest by distance
    let a = 0
    let b = data.length - 1
    while (a <= b) {
      const m = (a + b) >> 1
      if (data[m].d < dVal) a = m + 1
      else b = m - 1
    }
    let idx = Math.max(0, Math.min(data.length - 1, a))
    if (idx > 0 && Math.abs(data[idx - 1].d - dVal) < Math.abs(data[idx].d - dVal)) idx--
    setHover(idx)
  }

  const hp = hover != null ? data[hover] : null

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      >
        <path d={area} fill={color} fillOpacity={0.15} />
        <path d={line} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />

        {hp && (
          <>
            <line
              x1={x(hp.d)}
              x2={x(hp.d)}
              y1={PAD.top}
              y2={baseY}
              stroke="#9ca3af"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <circle cx={x(hp.d)} cy={y(hp.e)} r={3} fill={color} stroke="#fff" strokeWidth={1.5} />
          </>
        )}

        {/* axis labels */}
        <text x={PAD.left} y={H - 4} fontSize="9" fill="#9ca3af">0 km</text>
        <text x={W - PAD.right} y={H - 4} fontSize="9" fill="#9ca3af" textAnchor="end">
          {maxD.toFixed(1)} km
        </text>
        <text x={2} y={y(maxE) + 3} fontSize="9" fill="#9ca3af">{Math.round(maxE)}m</text>
        <text x={2} y={y(minE) + 3} fontSize="9" fill="#9ca3af">{Math.round(minE)}m</text>
      </svg>

      {hp && (
        <div
          className="absolute -top-1 px-2 py-1 bg-gray-900 text-white text-xs rounded pointer-events-none whitespace-nowrap"
          style={{ left: `${(x(hp.d) / W) * 100}%`, transform: 'translateX(-50%)' }}
        >
          {Math.round(hp.e)}m · {hp.d.toFixed(1)}km
        </div>
      )}
    </div>
  )
}

export default ElevationProfile
